import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, Package, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Product, ProductsQueryParams, PagedResponse } from '../../../types';
import { productsApi } from '../../../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

interface ProductsListProps {
  onEdit: (productId: string) => void;
  onDelete: () => void;
}

export const ProductsList: React.FC<ProductsListProps> = ({ onEdit, onDelete }) => {
  const [data, setData] = useState<PagedResponse<Product> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Query parameters state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, searchQuery, activeFilter]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params: ProductsQueryParams = {
        page,
        pageSize,
        ...(searchQuery && { search: searchQuery }),
        ...(activeFilter !== undefined && { isActive: activeFilter }),
      };
      
      const response = await productsApi.getAll(params);
      setData(response);
    } catch (err: any) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Error al cargar los productos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      return;
    }

    try {
      setDeletingId(id);
      await productsApi.delete(id);
      fetchProducts(); // Refresh list
      onDelete();
    } catch (err: any) {
      console.error('Error deleting product:', err);
      alert('Error al eliminar el producto');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1); // Reset to first page
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page
  };

  const handleActiveFilterChange = (value: string) => {
    if (value === 'all') {
      setActiveFilter(undefined);
    } else {
      setActiveFilter(value === 'true');
    }
    setPage(1); // Reset to first page
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
          <p className="text-slate-600">Cargando productos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Package className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-red-600 font-medium mb-2">Error al cargar los productos</p>
          <p className="text-slate-600 text-sm">{error}</p>
          <Button
            onClick={fetchProducts}
            className="mt-4"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre o código externo..."
              className="flex-1 bg-white border border-slate-300 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-10"
            />
            <Button type="submit">
              <Search size={18} className="mr-2" />
              <span className="hidden sm:inline">Buscar</span>
            </Button>
          </form>

          {/* Active Filter */}
          <select
            value={activeFilter === undefined ? 'all' : activeFilter.toString()}
            onChange={(e) => handleActiveFilterChange(e.target.value)}
            className="bg-white border border-slate-300 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 min-w-[140px]"
          >
            <option value="all">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {!data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Package className="h-12 w-12 mb-3" />
            <p className="text-lg font-medium">No hay productos</p>
            <p className="text-sm mt-2">
              {searchQuery || activeFilter !== undefined
                ? 'No se encontraron productos con los filtros aplicados'
                : 'Crea tu primer producto para comenzar'}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border-b border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((product) => (
                    <ContextMenu key={product.id}>
                      <ContextMenuTrigger asChild>
                        <TableRow 
                          onClick={() => onEdit(product.id)}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                        >
                          <TableCell className="font-mono text-xs">
                            {product.externalId || '-'}
                          </TableCell>
                          <TableCell className="font-medium">
                            {product.name}
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                product.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {product.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </TableCell>
                        </TableRow>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-48">
                        <ContextMenuItem onClick={() => onEdit(product.id)} className="cursor-pointer">
                          <Edit2 className="mr-2 h-4 w-4" />
                          <span>Editar producto</span>
                        </ContextMenuItem>
                        <ContextMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }} 
                          className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Eliminar producto</span>
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="bg-slate-50 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Results count */}
              <div className="text-sm text-slate-600">
                Mostrando {((data.page - 1) * data.pageSize) + 1} a {Math.min(data.page * data.pageSize, data.totalCount)} de {data.totalCount} productos
              </div>

              {/* Pagination controls */}
              <div className="flex items-center gap-3">
                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Por página:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="bg-white border border-slate-300 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPage(page - 1)}
                    disabled={!data.hasPreviousPage || isLoading}
                    title="Página anterior"
                  >
                    <ChevronLeft size={16} />
                  </Button>

                  <div className="px-3 py-2 text-sm text-slate-700 min-w-[100px] text-center">
                    Página {data.page} de {data.totalPages}
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPage(page + 1)}
                    disabled={!data.hasNextPage || isLoading}
                    title="Página siguiente"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
